// src/pages/Admin/Formation/TrainingArticlesAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";

import {
  getTrainingArticles,
  createTrainingArticle,
  updateTrainingArticle,
  deleteTrainingArticle,
  toggleTrainingArticlePublished,
  updateTrainingArticlePhoto,
} from "../../../services/trainingArticleServices";

import TrainingArticleFormModal from "./TrainingArticleFormModal";

import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSyncAlt,
  FaSearch,
  FaCopy,
  FaImage,
  FaEye,
  FaEyeSlash,
  FaPlayCircle,
  FaFilePowerpoint,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaNewspaper,
  FaCheckCircle,
  FaRegEdit,
} from "react-icons/fa";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const short = (s = "", n = 110) => {
  const t = String(s || "").replace(/<[^>]*>/g, "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
};

const formatDateFR = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
};

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

const CATEGORY_OPTIONS = [
  { value: "", label: "Toutes catégories" },
  { value: "DIRIGEANTS", label: "Dirigeants" },
  { value: "CERTIF_ASSO", label: "Certif Asso" },
];

const CATEGORY_LABEL = (v) =>
  v === "CERTIF_ASSO" ? "Certif Asso" : v === "DIRIGEANTS" ? "Dirigeants" : "—";

const publicationLabel = (value) => {
  if (value === "published") return "Publiés";
  if (value === "draft") return "Brouillons";
  return "Tous";
};

/* ================= UI atoms ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ total, publishedCount, draftCount }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Formation — Articles
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Gestion des contenus Dirigeants et Certif Asso, avec pilotage des
              brouillons, publications et médias associés.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[620px]">
            <CompactStat
              icon={<FaNewspaper />}
              label="Articles"
              value={total}
              accent="indigo"
            />
            <CompactStat
              icon={<FaCheckCircle />}
              label="Publiés"
              value={publishedCount}
              accent="emerald"
            />
            <CompactStat
              icon={<FaRegEdit />}
              label="Brouillons"
              value={draftCount}
              accent="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  q,
  setQ,
  category,
  setCategory,
  pub,
  setPub,
  onReset,
  onRefresh,
  onCreate,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),220px,220px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (titre, contenu, liens...)"
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={pub}
          onChange={(e) => setPub(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="all">Tous</option>
          <option value="published">Publiés</option>
          <option value="draft">Brouillons</option>
        </select>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            title="Recharger"
          >
            <FaSyncAlt />
            Rafraîchir
          </button>

          <button
            onClick={onCreate}
            className="inline-flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            <FaPlus />
            Nouvel article
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ published }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
        published
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
      ].join(" ")}
    >
      {published ? "Publié" : "Brouillon"}
    </span>
  );
}

function CategoryPill({ category }) {
  return (
    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
      {CATEGORY_LABEL(category)}
    </span>
  );
}

function ArticleThumb({ row }) {
  const cover = row.photo_url_1 || row.photo_url_2 || row.photo_url_3;

  if (!cover) {
    return (
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-400 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700">
        —
      </div>
    );
  }

  return (
    <img
      src={cover}
      alt=""
      className="h-11 w-11 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function MediaBadges({ row }) {
  const photosCount = [
    row.photo_url_1,
    row.photo_url_2,
    row.photo_url_3,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-wrap gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
        <FaImage className="opacity-70" />
        {photosCount}/3
      </span>

      {row.video_url ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          <FaPlayCircle />
          Vidéo
        </span>
      ) : null}

      {row.powerpoint_url ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-medium text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
          <FaFilePowerpoint />
          PPT
        </span>
      ) : null}
    </div>
  );
}

function ArticleActions({
  row,
  onEdit,
  onDuplicate,
  onUploadPhoto,
  onDelete,
}) {
  return (
    <div className="flex flex-wrap justify-center gap-1.5">
      <button
        onClick={() => onEdit(row)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-white transition hover:bg-indigo-700"
        title="Éditer"
      >
        <FaEdit />
      </button>

      <button
        onClick={() => onDuplicate(row)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        title="Dupliquer"
      >
        <FaCopy />
      </button>

      {[1, 2, 3].map((slot) => (
        <button
          key={slot}
          onClick={() => onUploadPhoto(row, slot)}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          title={`Uploader photo ${slot}`}
        >
          <FaImage />
          <span className="absolute -right-1 -top-1 rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] leading-none text-white">
            {slot}
          </span>
        </button>
      ))}

      <button
        onClick={() => onDelete(row)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-600 text-white transition hover:bg-red-700"
        title="Supprimer"
      >
        <FaTrash />
      </button>
    </div>
  );
}

function TrainingArticlesTableDesktop({
  rows,
  loading,
  onTogglePublished,
  onEdit,
  onDuplicate,
  onUploadPhoto,
  onDelete,
}) {
  return (
    <div className="hidden lg:block">
      <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full min-w-[1180px] table-fixed text-sm">
          <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
            <tr className="text-slate-600 dark:text-slate-300">
              <th className="w-[10%] px-3 py-3 font-semibold">Statut</th>
              <th className="w-[8%] px-3 py-3 font-semibold">Ordre</th>
              <th className="w-[28%] px-3 py-3 font-semibold">Article</th>
              <th className="w-[12%] px-3 py-3 font-semibold">Catégorie</th>
              <th className="w-[20%] px-3 py-3 font-semibold">Aperçu</th>
              <th className="w-[10%] px-3 py-3 font-semibold">Médias</th>
              <th className="w-[12%] px-3 py-3 font-semibold text-center">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  Chargement…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  Aucun article
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-3 py-3.5">
                    <div className="space-y-2">
                      <button
                        onClick={() => onTogglePublished(row)}
                        title={row.is_published ? "Dépublier" : "Publier"}
                        className={[
                          "inline-flex h-9 w-9 items-center justify-center rounded-full border transition",
                          row.is_published
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                        ].join(" ")}
                      >
                        {row.is_published ? <FaEye /> : <FaEyeSlash />}
                      </button>

                      <StatusPill published={!!row.is_published} />
                    </div>
                  </td>

                  <td className="px-3 py-3.5">
                    <span className="inline-flex rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {Number(row.sort_order || 0)}
                    </span>
                  </td>

                  <td className="px-3 py-3.5">
                    <div className="flex items-start gap-3">
                      <ArticleThumb row={row} />

                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                          {row.title || "Sans titre"}
                        </div>

                        {row.subtitle ? (
                          <div className="mt-1 line-clamp-1 text-[12px] text-slate-500 dark:text-slate-400">
                            {row.subtitle}
                          </div>
                        ) : null}

                        <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                          Mis à jour le {formatDateFR(row.updated_at)}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-3.5">
                    <CategoryPill category={row.category} />
                  </td>

                  <td className="px-3 py-3.5 text-[13px] leading-6 text-slate-700 dark:text-slate-200">
                    {short(row.content || "", 140)}
                  </td>

                  <td className="px-3 py-3.5">
                    <MediaBadges row={row} />
                  </td>

                  <td className="px-3 py-3.5">
                    <ArticleActions
                      row={row}
                      onEdit={onEdit}
                      onDuplicate={onDuplicate}
                      onUploadPhoto={onUploadPhoto}
                      onDelete={onDelete}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrainingArticlesCardsMobile({
  rows,
  loading,
  onTogglePublished,
  onEdit,
  onDuplicate,
  onUploadPhoto,
  onDelete,
}) {
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Aucun article
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const expanded = expandedIds.includes(row.id);

            return (
              <div
                key={row.id}
                className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-start gap-3">
                  <ArticleThumb row={row} />

                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                      {row.title || "Sans titre"}
                    </div>

                    {row.subtitle ? (
                      <div className="mt-1 line-clamp-1 text-[12px] text-slate-500 dark:text-slate-400">
                        {row.subtitle}
                      </div>
                    ) : null}

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusPill published={!!row.is_published} />
                      <CategoryPill category={row.category} />
                    </div>

                    <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                      Ordre {Number(row.sort_order || 0)} • MAJ le{" "}
                      {formatDateFR(row.updated_at)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onEdit(row)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <FaEdit />
                    Éditer
                  </button>

                  <button
                    onClick={() => toggleExpanded(row.id)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    {expanded ? <FaChevronUp /> : <FaChevronDown />}
                    {expanded ? "Réduire" : "Détails"}
                  </button>
                </div>

                {expanded ? (
                  <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                    <div className="space-y-4">
                      <div>
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Aperçu
                        </div>
                        <div className="text-[13px] leading-6 text-slate-700 dark:text-slate-200">
                          {short(row.content || "", 220)}
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Médias
                        </div>
                        <MediaBadges row={row} />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => onTogglePublished(row)}
                          className={[
                            "inline-flex h-10 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-semibold transition",
                            row.is_published
                              ? "bg-amber-500 text-white hover:bg-amber-600"
                              : "bg-emerald-600 text-white hover:bg-emerald-700",
                          ].join(" ")}
                        >
                          {row.is_published ? <FaEyeSlash /> : <FaEye />}
                          {row.is_published ? "Dépublier" : "Publier"}
                        </button>

                        <button
                          onClick={() => onDuplicate(row)}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaCopy />
                          Dupliquer
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {[1, 2, 3].map((slot) => (
                          <button
                            key={slot}
                            onClick={() => onUploadPhoto(row, slot)}
                            className="relative inline-flex h-10 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                            title={`Uploader photo ${slot}`}
                          >
                            <FaImage />
                            <span className="absolute -right-1 -top-1 rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] leading-none text-white">
                              {slot}
                            </span>
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => onDelete(row)}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        <FaTrash />
                        Supprimer
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page <span className="font-semibold text-slate-900 dark:text-white">{page}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
export default function TrainingArticlesAdmin() {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [pub, setPub] = useState("all");

  const debouncedQ = useDebounced(q, 300);

  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const data = await getTrainingArticles({});
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      addNotification("Erreur lors du chargement des articles.", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    let arr = [...items];

    if (category) {
      arr = arr.filter((x) => String(x.category || "") === String(category));
    }

    if (pub !== "all") {
      arr = arr.filter((x) =>
        pub === "published" ? Boolean(x.is_published) : !Boolean(x.is_published)
      );
    }

    if (debouncedQ.trim()) {
      const needle = normalize(debouncedQ);
      arr = arr.filter((x) =>
        [
          x.title,
          x.subtitle,
          x.content,
          x.video_url,
          x.powerpoint_url,
          x.category,
        ]
          .map((v) => normalize(v))
          .join(" ")
          .includes(needle)
      );
    }

    arr.sort((a, b) => {
      const so = Number(a.sort_order || 0) - Number(b.sort_order || 0);
      if (so !== 0) return so;

      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    });

    return arr;
  }, [items, debouncedQ, category, pub]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, category, pub]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  const publishedCount = useMemo(
    () => filtered.filter((x) => Boolean(x.is_published)).length,
    [filtered]
  );

  const draftCount = useMemo(
    () => filtered.filter((x) => !Boolean(x.is_published)).length,
    [filtered]
  );

  const resetFilters = () => {
    setQ("");
    setCategory("");
    setPub("all");
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleSubmitForm = async (payload) => {
    try {
      if (editing?.id) {
        await updateTrainingArticle(editing.id, payload);
        addNotification("Article mis à jour.", "success");
      } else {
        await createTrainingArticle(payload);
        addNotification("Article créé.", "success");
      }

      setFormOpen(false);
      setEditing(null);
      await fetchAll();
    } catch (error) {
      console.error(error);
      addNotification("Erreur lors de l’enregistrement de l’article.", "error");
    }
  };

  const handleDuplicate = (row) => {
    const clone = { ...row };
    delete clone.id;
    clone.is_published = false;
    clone.title = clone.title ? `${clone.title} (copie)` : "Copie";
    setEditing(clone);
    setFormOpen(true);
  };

  const handleTogglePublished = async (row) => {
    try {
      await toggleTrainingArticlePublished(row.id, !Boolean(row.is_published));
      setItems((prev) =>
        prev.map((x) =>
          x.id === row.id ? { ...x, is_published: !Boolean(x.is_published) } : x
        )
      );

      addNotification(
        row.is_published ? "Article dépublié." : "Article publié.",
        "success"
      );
    } catch (error) {
      console.error(error);
      addNotification("Erreur lors du changement de statut.", "error");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer l'article « ${row.title || "Sans titre"} » ?`)) {
      return;
    }

    try {
      await deleteTrainingArticle(row.id);
      addNotification("Article supprimé.", "success");
      await fetchAll();
    } catch (error) {
      console.error(error);
      addNotification("Erreur lors de la suppression.", "error");
    }
  };

  const pickAndUploadPhoto = async (row, slot) => {
    if (!row?.id) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        await updateTrainingArticlePhoto(
          row.id,
          slot,
          file,
          localStorage.getItem("token")
        );
        addNotification(`Photo ${slot} mise à jour.`, "success");
        await fetchAll();
      } catch (error) {
        console.error(error);
        addNotification(`Erreur lors de l’upload de la photo ${slot}.`, "error");
      }
    };

    input.click();
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          total={filtered.length}
          publishedCount={publishedCount}
          draftCount={draftCount}
        />

        <div className="mt-5">
          <FiltersBar
            q={q}
            setQ={setQ}
            category={category}
            setCategory={setCategory}
            pub={pub}
            setPub={setPub}
            onReset={resetFilters}
            onRefresh={fetchAll}
            onCreate={openCreate}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filtered.length} article{filtered.length > 1 ? "s" : ""}
          </span>

          {category && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Catégorie : {CATEGORY_LABEL(category)}
            </span>
          )}

          {pub !== "all" && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Statut : {publicationLabel(pub)}
            </span>
          )}

          {debouncedQ && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {q}
            </span>
          )}

          {!loading && filtered.length > 0 && (
            <span className="ml-auto text-xs">
              Affichage {pageItems.length} • page {safePage} / {totalPages}
            </span>
          )}
        </div>

        <div className="mt-5">
          <TrainingArticlesTableDesktop
            rows={pageItems}
            loading={loading}
            onTogglePublished={handleTogglePublished}
            onEdit={openEdit}
            onDuplicate={handleDuplicate}
            onUploadPhoto={pickAndUploadPhoto}
            onDelete={handleDelete}
          />

          <TrainingArticlesCardsMobile
            rows={pageItems}
            loading={loading}
            onTogglePublished={handleTogglePublished}
            onEdit={openEdit}
            onDuplicate={handleDuplicate}
            onUploadPhoto={pickAndUploadPhoto}
            onDelete={handleDelete}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={totalPages}
          setPage={setPage}
          loading={loading}
        />
      </div>

      <TrainingArticleFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        initialData={editing}
        onSubmit={handleSubmitForm}
      />
    </div>
  );
}